import type { CSSProperties } from 'react';
import {
  OVERLAY_KINDS,
  setOverlayOpacity,
  toggleOverlay,
  type OverlayKind,
  type TacticsOverlaysState,
} from '@4cm/core/tactics/overlays';
import { HudChip } from './HudChip';

/**
 * §17 analytical-overlay toggles for the tactics HUD.
 *
 * One HudChip per overlay kind — clicking toggles the kind on/off via
 * `toggleOverlay`. Active chips reveal an opacity slider below them
 * that writes through `setOverlayOpacity` (clamped [0, 1]).
 *
 * The stretch overlays (heatmap / passNet / pitchControl) carry a
 * 'preview' caption so coaches know the toggle exists but the
 * underlying computation is not shipped yet — the renderer is
 * expected to render a "coming soon" placeholder for those kinds.
 */
export interface OverlayTogglesProps {
  state: TacticsOverlaysState;
  onChange: (next: TacticsOverlaysState) => void;
}

// Display names — the persisted `OverlayKind` ids stay 'voronoi' etc.
// for schema/data stability, but the coach-facing UI uses friendlier
// language. 'Spacing' replaces 'Voronoi' since coaches read it as
// "how much pitch does each team control", not "Voronoi diagram".
const DISPLAY_NAMES: Record<OverlayKind, string> = {
  voronoi: 'Spacing',
  heatmap: 'Heatmap',
  passNet: 'Pass net',
  pitchControl: 'Pitch control',
  offsideLine: 'Offside line',
  defensiveShape: 'Defensive shape',
  halfSpaces: 'Half-spaces',
  teamShape: 'Team shape',
  posPlayGrid: 'Pos-play grid',
  xt: 'xT (danger)',
};

/** Stretch overlays surface a preview caption + a slightly different
 *  hint to the coach. Wave C wired up heatmap / passNet / pitchControl
 *  / offsideLine / defensiveShape alongside the existing voronoi, so
 *  the set is currently empty — kept around for future not-yet-shipped
 *  overlays so we don't have to re-introduce the chip styling. */
const PREVIEW_KINDS: ReadonlySet<OverlayKind> = new Set<OverlayKind>([]);

export function OverlayToggles({ state, onChange }: OverlayTogglesProps) {
  return (
    <div
      className="thud__overlay-toggles"
      data-testid="thud-overlay-toggles"
      role="group"
      aria-label="Analytical overlays"
    >
      {OVERLAY_KINDS.map((kind) => {
        const active = state.active.includes(kind);
        const label = DISPLAY_NAMES[kind];
        const isPreview = PREVIEW_KINDS.has(kind);
        const opacity = state.opacity[kind];
        // Match HudScrubber's `--val` CSS hook so the slider track fills
        // the same way as the run-playback scrubber.
        const sliderStyle: CSSProperties = {
          ['--val' as string]: opacity,
        } as CSSProperties;
        return (
          <div
            key={kind}
            className="thud__overlay-toggle"
            data-kind={kind}
            data-preview={isPreview || undefined}
            data-active={active || undefined}
          >
            <HudChip
              active={active}
              ariaLabel={`${label} overlay${isPreview ? ' (preview)' : ''}`}
              onClick={() => onChange(toggleOverlay(state, kind))}
            >
              {label}
              {isPreview ? (
                <span
                  className="thud__overlay-preview-tag"
                  aria-hidden="true"
                  data-testid={`thud-overlay-preview-${kind}`}
                >
                  {' '}
                  preview
                </span>
              ) : null}
            </HudChip>
            {active ? (
              <input
                type="range"
                className="thud__scrubber thud__overlay-opacity"
                data-testid={`thud-overlay-opacity-${kind}`}
                min={0}
                max={1}
                step={0.01}
                value={opacity}
                style={sliderStyle}
                aria-label={`${label} opacity: ${Math.round(opacity * 100)}%`}
                onChange={(e) =>
                  onChange(setOverlayOpacity(state, kind, Number(e.target.value)))
                }
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
